<template>
  <div class="audio-player-component">
    <div class="audio-controls">
      <img 
        v-if="!isPlaying" 
        src="../images/play.png" 
        alt="Play" 
        @click="playAudio" 
        class="control-icon"
        :class="{ 'disabled': !canPlay }"
      />
      <img 
        v-else 
        src="../images/pause.png" 
        alt="Pause" 
        @click="pauseAudio" 
        class="control-icon"
      />
    </div>
    <div class="audio-progress">
      <div class="audio-title">《{{ audioTitle }}》</div>
      <div class="progress-bar-container">
        <div class="progress-bar" :style="{ width: progressPercentage + '%' }"></div>
      </div>
      <span class="total-time">{{ formatTime(currentTime) }} / {{ formatTime(duration) }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AudioPlayer',
  props: {
    audioSrc: {
      type: String,
      required: true
    },
    audioTitle: {
      type: String,
      default: ''
    },
    canPlay: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      audioElement: null,
      isPlaying: false,
      currentTime: 0,
      duration: 0
    };
  },
  computed: {
    progressPercentage() {
      return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
    }
  },
  watch: {
    audioSrc: {
      immediate: true,
      handler(newSrc) {
        if (newSrc) {
          this.initAudio(newSrc);
        }
      }
    }
  },
  beforeDestroy() {
    this.cleanupAudio();
  },
  methods: {
    initAudio(src) {
      // Clean up previous audio element if exists
      this.cleanupAudio();
      
      // Create new audio element
      this.audioElement = new Audio(src);
      
      // Add event listeners
      this.audioElement.addEventListener('timeupdate', this.updateProgress);
      this.audioElement.addEventListener('loadedmetadata', () => {
        this.duration = this.audioElement.duration;
      });
      this.audioElement.addEventListener('ended', () => {
        this.isPlaying = false;
        this.currentTime = 0;
        this.$emit('audio-ended');
      });
      
      // Handle errors
      this.audioElement.addEventListener('error', (e) => {
        console.error('Audio error:', e);
      });
    },
    
    playAudio() {
      if (!this.canPlay || !this.audioElement) return;
      
      this.audioElement.play();
      this.isPlaying = true;
      this.$emit('play');
    },
    
    pauseAudio() {
      if (this.audioElement) {
        this.audioElement.pause();
        this.isPlaying = false;
        this.$emit('pause');
      }
    },
    
    updateProgress() {
      if (this.audioElement) {
        this.currentTime = this.audioElement.currentTime;
      }
    },
    
    cleanupAudio() {
      if (this.audioElement) {
        this.audioElement.removeEventListener('timeupdate', this.updateProgress);
        this.audioElement.pause();
        this.audioElement = null;
      }
    },
    
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  }
};
</script>

<style scoped>
.audio-player-component {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 520px;
  background-color: #FFC300;
  border-radius: 40px;
  padding: 8px 16px;
}

.audio-controls {
  display: flex;
  align-items: center;
  margin-right: 10px;
}

.control-icon {
  width: 46px;
  height: 46px;
  cursor: pointer;
}

.control-icon.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.audio-progress {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.total-time {
  font-size: 18px;
  color: #333;
}

.audio-title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.progress-bar-container {
  width: 100%;
  height: 6px;
  background-color: #B55641;
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #FFF6A1;
  border-radius: 3px;
  transition: width 0.1s linear;
}
</style>
